import { useEffect, useState } from "react";
import { Loader2, Save, Trash2, AlertTriangle, Upload, X, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { CONTACT_ICONS } from "@/components/ContactItems";
import { Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const FIELDS = [
  ["website_name", "Website Name"], ["tagline", "Tagline"], ["parent_company", "Parent Company"],
  ["phone1", "Contact Number 1"], ["phone2", "Contact Number 2"], ["email", "Email"],
  ["seo_title", "SEO Title"],
];

export default function AdminSettings() {
  const { setSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [demoCount, setDemoCount] = useState(null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
    api.get("/admin/demo-data").then((r) => setDemoCount(r.data.machines)).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const [uploading, setUploading] = useState("");
  const uploadAsset = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [key]: data.url }));
      toast.success("Uploaded — remember to Save");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(""); }
  };

  const clearDemo = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/admin/demo-data");
      toast.success(`Cleared ${data.deleted} demo listings`);
      setDemoCount(0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setClearing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-display text-2xl font-extrabold text-slate-900">Website Settings</h1>
      <p className="mt-1 text-slate-500">Configure branding, contact details and SEO.</p>

      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div className="grid gap-5 sm:grid-cols-2">
          <AssetUpload label="Logo" testid="logo" value={form.logo_url} uploading={uploading === "logo_url"}
            onUpload={(f) => uploadAsset("logo_url", f)} onClear={() => setForm({ ...form, logo_url: "" })}
            hint="Shown in the header instead of the MASHINIYA text. PNG/SVG with transparent background works best." />
          <AssetUpload label="Favicon" testid="favicon" value={form.favicon_url} uploading={uploading === "favicon_url"}
            onUpload={(f) => uploadAsset("favicon_url", f)} onClear={() => setForm({ ...form, favicon_url: "" })}
            hint="Small square icon shown in the browser tab (32×32 recommended)." small />
        </div>
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <Label>{label}</Label>
            <Input data-testid={`settings-${k}`} value={form[k] || ""} onChange={set(k)} />
          </div>
        ))}
        <div><Label>Address</Label><Textarea data-testid="settings-address" value={form.address || ""} onChange={set("address")} /></div>
        <div><Label>SEO Description</Label><Textarea data-testid="settings-seo_description" value={form.seo_description || ""} onChange={set("seo_description")} /></div>
        <div><Label>Footer Text</Label><Textarea data-testid="settings-footer_text" value={form.footer_text || ""} onChange={set("footer_text")} /></div>
        <button data-testid="settings-save" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-brand px-8 py-3 font-bold text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />} Save Settings
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-bold text-slate-900">Post-Sale Service Charge</h2>
        <p className="mt-1 text-sm text-slate-500">
          A <strong>fixed</strong> amount charged only after a Mashiniya-facilitated deal is successfully completed.
          This is not a percentage commission and does not depend on the sale price.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-[220px_1fr]">
          <div>
            <Label>Fixed Service Charge (₹)</Label>
            <Input data-testid="settings-service_charge_amount" type="number" min="0"
              value={form.service_charge_amount ?? 0}
              onChange={(e) => setForm({ ...form, service_charge_amount: e.target.value })} />
          </div>
          <div>
            <Label>Terms shown to sellers</Label>
            <Textarea data-testid="settings-service_charge_terms" rows={2}
              value={form.service_charge_terms || ""}
              onChange={set("service_charge_terms")} />
          </div>
        </div>
        <button data-testid="settings-save-charge" onClick={save} disabled={saving}
          className="mt-4 flex items-center gap-2 rounded-full bg-brand px-8 py-3 font-bold text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />} Save
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-bold text-slate-900">Contact &amp; Social Media</h2>
        <p className="mt-1 text-sm text-slate-500">
          One central place for phone, WhatsApp, email, address, Google Maps and social links.
          Enabled items appear on the Homepage, Contact page and Footer according to the visibility boxes.
          Drag rows (desktop) or use the arrows to change display order.
        </p>
        <ContactSocialEditor form={form} setForm={setForm} />
        <button data-testid="settings-save-contact" onClick={save} disabled={saving}
          className="mt-4 flex items-center gap-2 rounded-full bg-brand px-8 py-3 font-bold text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />} Save Changes
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-6">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle size={18} />
          <h2 className="font-display font-bold">Danger Zone — Demo Data</h2>
        </div>
        <p className="mt-2 text-sm text-red-600">
          {demoCount === null ? "Checking demo listings…" :
            demoCount === 0 ? "No demo listings remain. You're all set for real inventory." :
            `${demoCount} demo listings (used machines, new machines & spare parts) are currently live. Once you've added your real inventory, remove them all in one click.`}
        </p>
        {demoCount > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="clear-demo-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-2.5 text-sm font-bold text-white">
                <Trash2 size={16} /> Clear All Demo Listings
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all demo listings?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes all {demoCount} demo machines, new machines and spare parts (and their enquiries).
                  Your own real listings, sellers, categories and settings are NOT affected. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-clear-demo" onClick={clearDemo} disabled={clearing} className="bg-red-600">
                  {clearing ? "Clearing…" : "Yes, clear demo data"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

function ContactSocialEditor({ form, setForm }) {
  const [dragIdx, setDragIdx] = useState(null);
  const items = [...(form.contact_items || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const commit = (next) => setForm({ ...form, contact_items: next.map((it, i) => ({ ...it, order: i + 1 })) });
  const update = (id, patch) => commit(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const move = (id, dir) => {
    const i = items.findIndex((it) => it.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    commit(next);
  };
  const drop = (targetId) => {
    if (dragIdx === null) return;
    const from = items.findIndex((it) => it.id === dragIdx);
    const to = items.findIndex((it) => it.id === targetId);
    if (from < 0 || to < 0 || from === to) { setDragIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
    setDragIdx(null);
  };

  const VisBox = ({ item, field, label }) => (
    <label className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-slate-600">
      <input type="checkbox" data-testid={`contact-vis-${field}-${item.key}`} checked={!!item[field]}
        onChange={(e) => update(item.id, { [field]: e.target.checked })} className="h-3.5 w-3.5 accent-[hsl(var(--brand))]" />
      {label}
    </label>
  );

  const groups = [["contact", "CONTACT INFORMATION"], ["social", "SOCIAL MEDIA"]];
  return (
    <div className="mt-4 space-y-5">
      {groups.map(([kind, title]) => (
        <div key={kind}>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">{title}</p>
          <div className="space-y-2">
            {items.filter((it) => it.kind === kind).map((item) => {
              const Icon = CONTACT_ICONS[item.icon] || Globe;
              return (
                <div key={item.id} data-testid={`contact-item-${item.key}`} draggable
                  onDragStart={() => setDragIdx(item.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(item.id)}
                  className={`rounded-lg border p-3 ${item.enabled ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-70"}`}>
                  <div className="flex items-center gap-2">
                    <GripVertical size={15} className="cursor-grab text-slate-300" />
                    <Icon size={16} className="text-slate-500" />
                    {item.key === "other" ? (
                      <Input value={item.label} onChange={(e) => update(item.id, { label: e.target.value })} className="h-7 w-32 text-sm font-bold" />
                    ) : (
                      <span className="text-sm font-bold text-slate-800">{item.label}</span>
                    )}
                    <span className="ml-auto flex items-center gap-1">
                      <button type="button" data-testid={`contact-up-${item.key}`} onClick={() => move(item.id, -1)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><ChevronUp size={15} /></button>
                      <button type="button" data-testid={`contact-down-${item.key}`} onClick={() => move(item.id, 1)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><ChevronDown size={15} /></button>
                    </span>
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs font-bold text-slate-600">
                      <input type="checkbox" data-testid={`contact-enabled-${item.key}`} checked={!!item.enabled}
                        onChange={(e) => update(item.id, { enabled: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--brand))]" />
                      {item.enabled ? "ON" : "OFF"}
                    </label>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
                    <Input data-testid={`contact-value-${item.key}`} value={item.value || ""}
                      onChange={(e) => update(item.id, { value: e.target.value })}
                      placeholder={item.key === "email" ? "name@example.com" : item.key === "address" ? "Full business address" : item.key.startsWith("phone") || item.key === "whatsapp" ? "+91 …" : "https://…"} />
                    <div className="flex flex-wrap gap-3">
                      <VisBox item={item} field="show_home" label="Homepage" />
                      <VisBox item={item} field="show_contact" label="Contact Us" />
                      <VisBox item={item} field="show_footer" label="Footer" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function AssetUpload({ label, testid, value, uploading, onUpload, onClear, hint, small }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1.5 flex items-center gap-3">
        <div className={`flex ${small ? "h-12 w-12" : "h-12 w-28"} items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50`}>
          {value ? <img src={fileUrl(value)} alt={label} className="h-full w-full object-contain" /> : <span className="text-[10px] text-slate-400">None</span>}
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload size={15} />} Upload
          <input data-testid={`settings-${testid}-upload`} type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files[0])} />
        </label>
        {value && <button data-testid={`settings-${testid}-clear`} onClick={onClear} className="rounded-full border border-red-200 p-2 text-red-500"><X size={15} /></button>}
      </div>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}
